"use client";

/**
 * `RelationField` — async picker for a single related-entity reference.
 * The persisted form-state value is the related entity's `value` (an
 * id, in practice), or `null` when nothing is selected. The control
 * surfaces three states:
 *
 * 1. **Empty** — a search input with the field placeholder. As the user
 *    types, a debounced `search(query)` runs and the popover renders
 *    matching options.
 * 2. **Selected** — the input is replaced by a chip showing the display
 *    label and a clear (×) button.
 * 3. **Hydrating** — when the form mounts with a saved value but no
 *    recent search hit, we call `getDisplayLabel(value)` once to fetch
 *    the chip's label asynchronously. Until that resolves the chip
 *    shows the raw value (typically an id) — better than blanking the
 *    UI or showing a spinner since the form is still fully usable.
 *
 * Why a debounced async search instead of an in-memory filter:
 * RelationField is the field type that bridges to live data — partners,
 * accounts, products. Caller-supplied `search` typically hits the
 * server (or an indexed local cache); calling it on every keystroke
 * would saturate the network. A 250ms debounce trims the call rate to
 * one request per pause without making the UI feel slow.
 *
 * The debounce uses a small inline `useDebounce` helper rather than
 * pulling in a third-party hook — the implementation is six lines and
 * doesn't earn a dependency.
 *
 * Why `useWatch` + `setValue` rather than `Controller`: rhf's
 * `Controller` runs its `render` function on every form-state change,
 * so any `useEffect`/`useState` we'd want to put inside the render
 * function would either violate the rules-of-hooks or get re-allocated
 * unnecessarily. Reading the value through `useWatch` and committing
 * via `setValue` keeps every hook at the top level of the component
 * function where React expects them.
 *
 * Accessibility: the input has `role="combobox"` and `aria-expanded`
 * reflecting whether the popover is open. Each result is `role="option"`
 * so screen readers announce them as a listbox. The chip has an
 * `aria-label`d clear button ("Clear partner"). Empty-state is a
 * dedicated message, not a silent absence — "No results" beats a
 * confusing empty popover.
 */

import * as React from "react";
import { useWatch } from "react-hook-form";
import { X } from "lucide-react";
import { cn, Input } from "@aio/platform-ui";
import { useFormContext } from "../form-context";
import type { FieldOption, RelationFieldDef } from "../types";
import { FieldShell } from "./field-shell";
import { readErrorMessage } from "./read-error";
import { resolveFieldPath } from "./path";

export interface RelationFieldProps {
  field: RelationFieldDef;
  /** Path prefix from a parent {@link RepeaterField}. See `text-field.tsx`. */
  pathPrefix?: string;
}

/**
 * Tiny debounce hook. Returns a value that lags `value` by `delay` ms.
 * Reset on every change — only the final value after the pause window
 * propagates to consumers.
 *
 * Inline rather than a shared hook because: (1) it's six lines, (2) the
 * surrounding RelationField is the only consumer, and (3) the callers
 * who'd reach for it next (search-as-you-type tables, autocomplete
 * inputs) all want slightly different debounce semantics (leading-edge
 * vs trailing, max-wait, cancel) that a shared "useDebounce" would have
 * to either ignore or expose as a confusing surface.
 */
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(value);
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);
  return debounced;
}

/**
 * Chip rendered when a value is selected. Mirrors the styling of the
 * `MultiSelectField` chip but stands alone (single-value field), with a
 * caller-friendly clear button instead of a per-chip remove. The
 * `aria-label` of the clear button names the field so a screen reader
 * announces "Clear Partner button" rather than the ambiguous "Clear".
 */
function SelectedChip({
  label,
  fieldLabel,
  onClear,
  disabled,
}: {
  label: string;
  fieldLabel: string;
  onClear: () => void;
  disabled: boolean;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 rounded-md border border-border bg-muted px-3 py-1.5 text-sm font-medium text-fg",
        disabled && "opacity-60",
      )}
      data-testid="relation-chip"
    >
      <span className="truncate">{label}</span>
      <button
        type="button"
        aria-label={`Clear ${fieldLabel}`}
        onClick={onClear}
        disabled={disabled}
        className="rounded-sm p-0.5 text-fg-muted hover:bg-bg hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        <X aria-hidden="true" className="size-3.5" />
      </button>
    </span>
  );
}

export function RelationField({
  field,
  pathPrefix,
}: RelationFieldProps): React.ReactElement | null {
  const { form } = useFormContext();
  const path = resolveFieldPath(field.name, pathPrefix);

  // Live-read the form-state value at the field's path. Equivalent to
  // `Controller`'s render-prop binding but lets us put hooks at the top
  // level instead of nested inside a render callback.
  const watchedValue = useWatch({ control: form.control, name: path });
  const selected =
    typeof watchedValue === "string" && watchedValue.length > 0
      ? watchedValue
      : null;

  const [query, setQuery] = React.useState("");
  const debouncedQuery = useDebounce(query, 250);
  const [results, setResults] = React.useState<FieldOption[]>([]);
  const [open, setOpen] = React.useState(false);
  const [hydratedLabel, setHydratedLabel] = React.useState<string | null>(null);
  // The "in-flight" search call's identity. Defends against a stale
  // resolver clobbering a fresher one when fast typing outpaces the
  // debounce window. The ref holds the increment of the latest call; a
  // resolver checks against its captured value before updating state.
  const searchSeqRef = React.useRef(0);
  const hydrationDoneRef = React.useRef(false);

  // Trigger a search whenever the debounced query changes AND the input
  // is still active (no value selected). Empty queries also fire —
  // callers may want to surface "recent" or "top N" results.
  React.useEffect(() => {
    if (selected !== null) return;
    const seq = ++searchSeqRef.current;
    void field
      .search(debouncedQuery)
      .then((next) => {
        if (seq !== searchSeqRef.current) return;
        setResults(next);
      })
      .catch(() => {
        // Search errors are surfaced as an empty result set rather than
        // a thrown render — the field stays usable and the caller's
        // onError plumbing can log it.
        if (seq !== searchSeqRef.current) return;
        setResults([]);
      });
  }, [debouncedQuery, selected, field]);

  // One-shot hydration: when the form mounts with a saved value and the
  // caller provides `getDisplayLabel`, fetch the label for the chip.
  // Without `getDisplayLabel` the chip falls back to the raw value —
  // better than a blocking spinner.
  React.useEffect(() => {
    if (hydrationDoneRef.current) return;
    if (selected === null) return;
    if (!field.getDisplayLabel) return;
    hydrationDoneRef.current = true;
    void field
      .getDisplayLabel(selected)
      .then((label) => {
        setHydratedLabel(label);
      })
      .catch(() => {
        setHydratedLabel(null);
      });
  }, [selected, field]);

  if (field.condition && !field.condition(form.getValues())) {
    return null;
  }

  const errorMessage = readErrorMessage(form.formState.errors, path);
  const isDisabled = field.disabled === true;

  const commit = (option: FieldOption) => {
    form.setValue(path, option.value, { shouldDirty: true, shouldTouch: true });
    setHydratedLabel(option.label);
    setQuery("");
    setOpen(false);
  };

  const clear = () => {
    form.setValue(path, null, { shouldDirty: true, shouldTouch: true });
    setHydratedLabel(null);
    setQuery("");
  };

  return (
    <FieldShell
      htmlFor={path}
      label={field.label}
      {...(field.description !== undefined ? { description: field.description } : {})}
      {...(errorMessage !== undefined ? { error: errorMessage } : {})}
    >
      {selected !== null ? (
        <SelectedChip
          label={hydratedLabel ?? selected}
          fieldLabel={field.label}
          onClear={clear}
          disabled={isDisabled}
        />
      ) : (
        <div className="relative">
          <Input
            id={path}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            aria-controls={`${path}-listbox`}
            aria-invalid={errorMessage !== undefined ? true : undefined}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
            }}
            onFocus={() => {
              setOpen(true);
            }}
            onBlur={(event) => {
              // Delay the close so a click on a result registers before
              // the popover unmounts. The `relatedTarget` approach is
              // more correct but jsdom doesn't always populate it during
              // user-event clicks.
              const next = event.relatedTarget as HTMLElement | null;
              if (next?.dataset["relationOption"] === "true") return;
              setTimeout(() => {
                setOpen(false);
              }, 100);
            }}
            disabled={isDisabled}
            {...(field.placeholder !== undefined
              ? { placeholder: field.placeholder }
              : {})}
          />
          {open && results.length > 0 ? (
            <ul
              id={`${path}-listbox`}
              role="listbox"
              className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-auto rounded-md border border-border bg-bg-elevated p-1 text-sm text-fg shadow-md"
            >
              {results.map((option) => (
                <li key={option.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    data-relation-option="true"
                    onMouseDown={(event) => {
                      // Prevent the input from blurring (which would race
                      // the click handler and close the popover before
                      // the option commits).
                      event.preventDefault();
                    }}
                    onClick={() => {
                      commit(option);
                    }}
                    className="block w-full rounded-sm px-3 py-1.5 text-left hover:bg-muted focus-visible:outline-none focus-visible:bg-muted"
                  >
                    {option.label}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {open && results.length === 0 && debouncedQuery.length > 0 ? (
            <div
              id={`${path}-listbox`}
              role="status"
              className="absolute left-0 right-0 top-full z-50 mt-1 rounded-md border border-border bg-bg-elevated p-3 text-sm text-fg-muted shadow-md"
            >
              No results.
            </div>
          ) : null}
        </div>
      )}
    </FieldShell>
  );
}
